import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FeeDetailsModal = ({ isOpen, onClose, feeData }) => {
  if (!isOpen || !feeData) return null;

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    })?.format(amount);
  };

  const formatTonnage = (tonnage) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })?.format(tonnage);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Icon name="FileText" size={20} color="#3B82F6" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">
                Fee Calculation Details
              </h2>
              <p className="text-sm text-slate-600">
                {feeData?.month} - {feeData?.invoiceNumber}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-600 mb-1">Total Tonnage</p>
              <p className="text-2xl font-bold text-slate-900">
                {formatTonnage(feeData?.totalTonnage)}
                <span className="text-sm font-normal text-slate-600 ml-1">
                  MT
                </span>
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-600 mb-1">Fee Rate</p>
              <p className="text-2xl font-bold text-slate-900">
                {formatCurrency(feeData?.feeRate)}
                <span className="text-sm font-normal text-slate-600 ml-1">
                  /MT
                </span>
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-600 mb-1">Shipments</p>
              <p className="text-2xl font-bold text-slate-900">
                {feeData?.shipmentsCount}
              </p>
            </div>
          </div>

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900">
                Shipment Breakdown
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">
                      Vessel
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">
                      Supplier
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">
                      Buyer
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-600">
                      Tonnage (MT)
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-600">
                      Fee Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {feeData?.shipments?.map((shipment, index) => (
                    <tr key={index} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm text-slate-900">
                        {shipment?.vessel}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {shipment?.supplier}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {shipment?.buyer}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-900 text-right font-medium">
                        {formatTonnage(shipment?.tonnage)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-900 text-right font-medium">
                        {formatCurrency(shipment?.feeAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900">
                Fee Calculation Summary
              </h3>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-slate-600">
                  Gross Fee ({formatTonnage(feeData?.totalTonnage)} MT ×{' '}
                  {formatCurrency(feeData?.feeRate)})
                </span>
                <span className="text-sm font-semibold text-slate-900">
                  {formatCurrency(feeData?.grossFee)}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-t border-slate-100">
                <span className="text-sm text-slate-600">
                  Advance Payment (33%)
                </span>
                <span className="text-sm font-semibold text-green-600">
                  -{formatCurrency(feeData?.advancePayment)}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-t border-slate-100">
                <span className="text-sm text-slate-600">
                  Reimbursable Costs
                </span>
                <span className="text-sm font-semibold text-red-600">
                  -{formatCurrency(feeData?.reimbursableCosts)}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 bg-blue-50 rounded-lg px-3 border-t-2 border-blue-200">
                <span className="text-base font-semibold text-slate-900">
                  Net Balance Due
                </span>
                <span className="text-xl font-bold text-blue-600">
                  {formatCurrency(feeData?.netBalance)}
                </span>
              </div>
            </div>
          </div>

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900">
                Reimbursable Costs Breakdown
              </h3>
            </div>
            <div className="p-4 space-y-2">
              {feeData?.costBreakdown?.map((cost, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0"
                >
                  <div className="flex items-center gap-2">
                    <Icon name="Receipt" size={16} color="#64748B" />
                    <span className="text-sm text-slate-700">
                      {cost?.category}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-slate-900">
                    {formatCurrency(cost?.amount)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
            <Button
              variant="outline"
              iconName="Download"
              onClick={() => console.log('Export PDF')}
            >
              Export PDF
            </Button>
            <Button
              variant="default"
              iconName="Send"
              onClick={() => console.log('Send Invoice')}
            >
              Send Invoice
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeeDetailsModal;